import React, { useState, useEffect, useCallback } from "react";
import {
  fetchAllCompanies,
  createNewCompany,
  updateCompany,
  deleteCompany,
} from "../../apis/CompanyApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useTheme } from "../Theme/ThemeContext";
import { SkeletonCompanyGrid } from "../Skeletons";

export default function AdminCompaniesTab() {
  const { user } = useAuth();
  const { successToast, errorToast } = useToastHelpers();
  const { currentTheme } = useTheme();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const ITEMS_PER_PAGE = 12;

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllCompanies();
      setCompanies(Array.isArray(data) ? data : data?.companies || []);
    } catch (error) {
      errorToast("Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, [errorToast]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Filter companies
  const filteredCompanies = companies.filter((c) => {
    return (
      !search || c.name?.toLowerCase().includes(search.toLowerCase())
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE);
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleDelete = async (companyId) => {
    if (!window.confirm("Are you sure you want to delete this company?"))
      return;

    setDeletingId(companyId);
    try {
      await deleteCompany(companyId, user.token);
      successToast("Company deleted successfully");
      setCompanies((prev) => prev.filter((c) => c.id !== companyId));
    } catch (error) {
      errorToast("Failed to delete company");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddSuccess = (newCompany) => {
    setCompanies((prev) => [newCompany, ...prev]);
    setShowAddModal(false);
    successToast("Company created successfully");
  };

  const handleEditSuccess = (updatedCompany) => {
    setCompanies((prev) =>
      prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c))
    );
    setEditingCompany(null);
    successToast("Company updated successfully");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div>
      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="ca-card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <i className="ri-building-line text-2xl text-indigo-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Companies
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {companies.length}
              </p>
            </div>
          </div>
        </div>
        <div className="ca-card p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-add-circle-line text-2xl text-green-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Add New Company
                </p>
                <p className="text-sm text-gray-400">
                  Click the button to add
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="ca-btn-primary px-4 py-2 text-sm"
            >
              <i className="ri-add-line mr-1"></i> Add
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="ca-card p-6 mb-8">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Companies
          </label>
          <div className="relative">
            <input
              type="search"
              placeholder="Search by company name..."
              className="ca-input pl-10 text-sm"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="ca-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Companies ({filteredCompanies.length})
          </h3>
        </div>

        {loading ? (
          <div className="p-6">
            <SkeletonCompanyGrid count={12} />
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-building-line text-5xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No companies found</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 ca-btn-primary px-4 py-2 text-sm"
            >
              Add First Company
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedCompanies.map((company) => (
                <div
                  key={company.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-3">
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-12 h-12 rounded-lg bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center text-white font-bold text-lg ${
                        company.logo ? "hidden" : ""
                      }`}
                    >
                      {company.name?.charAt(0) || "C"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">
                        {company.name}
                      </h4>
                      <p className="text-xs text-gray-500">
                        Added {formatDate(company.createdAt)}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => setEditingCompany(company)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <i className="ri-edit-line"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(company.id)}
                      disabled={deletingId === company.id}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      {deletingId === company.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-delete-bin-line"></i>
                      )}
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                  {Math.min(
                    currentPage * ITEMS_PER_PAGE,
                    filteredCompanies.length
                  )}{" "}
                  of {filteredCompanies.length} companies
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <CompanyModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {/* Edit Modal */}
      {editingCompany && (
        <CompanyModal
          company={editingCompany}
          onClose={() => setEditingCompany(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}

function CompanyModal({ company, onClose, onSuccess }) {
  const { user } = useAuth();
  const { errorToast } = useToastHelpers();
  const isEdit = Boolean(company);

  const [formData, setFormData] = useState({
    name: company?.name || "",
    logo: company?.logo || "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      errorToast("Company name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        logo: formData.logo.trim(),
      };

      let result;
      if (isEdit) {
        result = await updateCompany(company.id, payload, user.token);
      } else {
        result = await createNewCompany(payload, user.token);
      }
      onSuccess(result);
    } catch (error) {
      errorToast(
        error.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} company`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isEdit ? "Edit Company" : "Add New Company"}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Google"
                className="ca-input"
              />
            </div>

            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo URL
              </label>
              <input
                type="url"
                name="logo"
                value={formData.logo}
                onChange={handleChange}
                placeholder="https://..."
                className="ca-input"
              />
              {formData.logo && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Preview:</span>
                  <img
                    src={formData.logo}
                    alt="Logo preview"
                    className="w-8 h-8 object-contain rounded bg-gray-100"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="ca-btn-primary px-6 py-2 inline-flex items-center gap-2"
            >
              {saving && <i className="ri-loader-4-line animate-spin"></i>}
              {saving
                ? "Saving..."
                : isEdit
                ? "Save Changes"
                : "Create Company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
